import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Space, Typography, Skeleton, Row, Col, Tooltip, List } from 'antd';
import { Link } from 'react-router-dom';
import {
  IdcardOutlined,
  StarOutlined,
  AlertOutlined,
  FormOutlined,
  QuestionCircleOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';

import * as fetchPeople from '~/store/fetchActions/fetchPeople';
import { selectLoadingState } from '~/store/selectors';
import SoluterCompetences from './SoluterCompetences';

import { Tag, Button as CustomButton } from '~/components';
import { colors, people } from '~/constants';
import './styles.scss';
import AddResume from '~/container/Resume/AddResume';

const { Title, Text } = Typography;

export default function SoluterBasicInfo({ email }) {
  const {
    isLoadingSoluterBasicData,
    isLoadingSoluterCompetences,
    isLoadingBadgesPhotos,
    isLoadingSoluterResume,
  } = useSelector(selectLoadingState);
  const {
    interests,
    minibio,
    competences,
    technicalSkills,
    badges,
    resume,
    employeeNumber,
  } = useSelector(state => state.people.soluter);

  const [isResumeVisible, setIsResumeVisible] = useState(false);
  const handleClickIsResumeVisible = () => {
    setIsResumeVisible(true);
  };

  const dispatch = useDispatch();

  const handleClickDownloadResume = useCallback(() => {
    dispatch(
      fetchPeople.downloadSoluterResume(resume.employeeNumber, resume.fileName),
    );
  }, [resume, dispatch]);

  const handleDeleteResume = useCallback(() => {
    dispatch(fetchPeople.deleteSoluterResume(resume.employeeNumber));
  }, [resume, dispatch]);

  useEffect(() => {
    dispatch(fetchPeople.getSoluterJourney(email));
    dispatch(fetchPeople.getSoluterCompetences(email));
  }, [dispatch, email]);

  useEffect(() => {
    if (employeeNumber) dispatch(fetchPeople.getSoluterResume(employeeNumber));
  }, [dispatch, employeeNumber]);

  useEffect(() => {
    if (badges && !badges[0]?.thumb) {
      badges.map(item => dispatch(fetchPeople.getBadgePhotoById(item)));
    }
  }, [badges, dispatch]);

  return (
    <div>
      <Row>
        <Col span={24}>
          <div>
            <Title level={5}>Quem eu sou</Title>
            <div style={{ display: 'flex' }}>
              <Skeleton
                paragraph={{ rows: 0 }}
                loading={isLoadingSoluterBasicData}
              >
                {minibio ? (
                  <p>{minibio}</p>
                ) : (
                  <p className="soluter-profile__missing-info">
                    <IdcardOutlined /> Este usuário ainda não escreveu sobre si.
                  </p>
                )}
              </Skeleton>
            </div>
          </div>
          <div className="soluter-profile__item">
            <div className="soluter-profile__overview">
              <Title level={5}>Competência Técnica</Title>
              <Tooltip
                title={people.INFO_EXPERIENCES_SOLUTER}
                placement="right"
              >
                <QuestionCircleOutlined />
              </Tooltip>
            </div>

            <div style={{ display: 'flex' }}>
              <Skeleton
                paragraph={{ rows: 0 }}
                loading={isLoadingSoluterBasicData}
              >
                <Space size={[8, 16]} wrap>
                  {technicalSkills && technicalSkills?.length > 0 ? (
                    technicalSkills?.map(ov => (
                      <Tag
                        key={ov}
                        tag={{ tag: ov }}
                        backgroundColor={colors.SKILLS}
                        textColor="white"
                      />
                    ))
                  ) : (
                    <p className="soluter-profile__missing-info">
                      <StarOutlined /> Nenhuma habilidade registrada (ainda).
                    </p>
                  )}
                </Space>
              </Skeleton>
            </div>
          </div>
          <div className="soluter-profile__item">
            <Title level={5}>Interesses</Title>
            <Skeleton
              paragraph={{ rows: 0 }}
              loading={isLoadingSoluterBasicData}
            >
              <Space size={[8, 16]} wrap>
                {interests && interests?.length > 0 ? (
                  interests?.map(interest => (
                    <Tag
                      key={interest}
                      tag={{ tag: interest }}
                      backgroundColor={colors.TECH}
                      textColor="white"
                    />
                  ))
                ) : (
                  <p className="soluter-profile__missing-info">
                    <AlertOutlined /> Nenhum interesse registrado (ainda).
                  </p>
                )}
              </Space>
            </Skeleton>
          </div>
          <div className="soluter-profile__item">
            <div className="certification-title">
              <Title level={5}>Certificações</Title>
            </div>
            <Skeleton
              paragraph={{ rows: 0 }}
              loading={isLoadingSoluterCompetences}
            >
              {competences?.length > 0 ? (
                <SoluterCompetences competences={competences} />
              ) : (
                <p className="soluter-profile__missing-info">
                  <FormOutlined /> Nenhuma competência registrada (ainda).
                </p>
              )}
            </Skeleton>
          </div>
          <div className="soluter-profile__item">
            <div className="badges-title">
              <Title level={5}>Badges</Title>
            </div>
            <Skeleton paragraph={{ rows: 0 }} loading={isLoadingBadgesPhotos}>
              <div className="badges-wrapper">
                {badges?.length > 0 ? (
                  badges?.map(badge => (
                    <Link
                      key={badge.badgeId}
                      className="badges-card"
                      to={`/badges/edit/${badge?.badgeId}`}
                    >
                      <div className="badges-header">
                        <span>
                          <img
                            src={badge?.thumb}
                            alt="imagem"
                            className="details-card__img"
                          />
                        </span>
                        <strong>{badge?.title}</strong>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="soluter-profile__missing-info">
                    <FormOutlined /> Nenhuma badge registrada (ainda).
                  </p>
                )}
              </div>
            </Skeleton>
          </div>
          <div className="soluter-profile__item">
            <div className="soluter-profile__resume">
              <Title level={5}>Currículo</Title>
              {employeeNumber && (
                <AddResume
                  employeeNumber={employeeNumber}
                  isVisible={isResumeVisible}
                  setVisible={setIsResumeVisible}
                />
              )}

              {!resume && (
                <CustomButton
                  style={{ marginLeft: 5, fontSize: 16, marginBottom: 6 }}
                  type="add"
                  isButton
                  tooltipIdentifier="Currículo"
                  handleClick={handleClickIsResumeVisible}
                />
              )}
            </div>
            {isLoadingSoluterResume ? (
              <Skeleton.Button
                style={{ width: 256, height: 48 }}
                loading={isLoadingSoluterResume}
              />
            ) : (
              <>
                {resume ? (
                  <List.Item>
                    <div className="soluter-resume" style={{ minHeight: 48 }}>
                      <div className="soluter-resume__name">
                        <FilePdfOutlined
                          style={{
                            fontSize: 18,
                          }}
                          className="icon"
                        />
                        <Text ellipsis strong style={{ width: 190 }}>
                          {resume.fileName}
                        </Text>
                      </div>
                      <div className="soluter-resume__actions">
                        <CustomButton
                          type="download"
                          isButton
                          tooltipIdentifier="Currículo"
                          handleClick={handleClickDownloadResume}
                          buttonStyle={{
                            fontSize: 14,
                          }}
                        />
                        <CustomButton
                          type="delete"
                          isButton
                          tooltipIdentifier="Currículo"
                          title="currículo"
                          messageText="O currículo será excluido permanentemente. Não será possível reverter esta ação."
                          handleDelete={handleDeleteResume}
                          buttonStyle={{
                            fontSize: 14,
                          }}
                        />
                      </div>
                    </div>
                  </List.Item>
                ) : (
                  <p className="soluter-profile__missing-info">
                    <FormOutlined /> Nenhum currículo cadastrado (ainda).
                  </p>
                )}
              </>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
}

SoluterBasicInfo.propTypes = {
  email: PropTypes.string.isRequired,
};
